import { useRef, useState } from 'react';
import type { FocusEvent } from 'react';
import type {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
} from '@mui/material';

import {
  AUTOCOMPLETE_FOCUS_IDLE,
  AutocompleteFocusEvent,
  autocompleteAcceptsValueChange,
  autocompleteInputValue,
  autocompleteQuery,
  focusCrossesTheControl,
  nextAutocompleteFocusState,
} from './autocompleteFocus';

interface Options {
  /**
   * Etiqueta de lo elegido, que el input muestra fuera de la sesión de
   * tipeo. `''` cuando no hay nada elegido y en los multi-select (sus
   * selecciones son chips).
   */
  selectedLabel: string;
  /**
   * Qué buscar. Se llama SOLO cuando el texto a buscar cambia de verdad, y
   * con `''` al salir del campo: el que la recibe suele ser el `setQuery` de
   * un `useDebouncedState` (búsqueda remota). Los pickers que filtran del
   * lado del cliente no la necesitan: les alcanza con leer `query`.
   */
  onQueryChange?: (query: string) => void;
}

/**
 * Cablea `autocompleteFocus.ts` con un `<Autocomplete>` de MUI. Lo que
 * devuelve se pasa tal cual al control:
 *
 * ```tsx
 * const focus = useAutocompleteFocus({ selectedLabel: value?.name ?? '', onQueryChange: setQuery });
 * <Autocomplete
 *   value={value}
 *   inputValue={focus.inputValue}
 *   onFocus={focus.onFocus}
 *   onBlur={focus.onBlur}
 *   onInputChange={focus.onInputChange}
 *   onChange={(_e, next, reason) => { if (focus.commitValueChange(reason)) onChange(next); }}
 *   loading={focus.editing && isFetching}
 * />
 * ```
 *
 * `onFocus`/`onBlur` van al Autocomplete (o sea, a su div raíz) y no al
 * `TextField` de `renderInput`: los eventos de foco burbujean, y el raíz es
 * el único lugar que ve también el foco que se va a la X de limpiar sin
 * salir del control.
 *
 * El estado vive ADEMÁS en un ref porque MUI emite `onInputChange` y
 * `onChange` uno atrás del otro dentro del mismo evento, y el segundo tiene
 * que ver lo que dejó el primero — un `useState` recién seteado todavía no
 * se ve en ese mismo batch de React.
 *
 * `editing` (hay sesión de tipeo) no es `focused` (el control tiene el
 * foco): después de elegir, MUI deja el input enfocado mostrando la
 * etiqueta elegida, y lo que se teclee ahí mismo empieza una búsqueda
 * limpia sin pasar por un blur (invariante 4 de `autocompleteFocus.ts`).
 */
export function useAutocompleteFocus({ selectedLabel, onQueryChange }: Options) {
  const [state, setState] = useState(AUTOCOMPLETE_FOCUS_IDLE);
  const latest = useRef(state);
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;
  // Por ref y no por closure: el `onInputChange` que MUI dispara al elegir
  // llega en el mismo evento que el `onChange`, antes de que el formulario
  // haya re-renderizado con la etiqueta nueva.
  const selectedLabelRef = useRef(selectedLabel);
  selectedLabelRef.current = selectedLabel;

  const apply = (event: AutocompleteFocusEvent) => {
    const next = nextAutocompleteFocusState(latest.current, event);
    if (next === latest.current) return;
    const queryChanged = autocompleteQuery(next) !== autocompleteQuery(latest.current);
    latest.current = next;
    setState(next);
    if (queryChanged) onQueryChangeRef.current?.(autocompleteQuery(next));
  };

  return {
    /** El asesor está tipeando acá: úsese para aislar el spinner del campo. */
    editing: state.editing,
    /**
     * Lo que hay que buscar AHORA (`''` fuera de la sesión de tipeo). Es lo
     * mismo que recibe `onQueryChange`, para los pickers que filtran del lado
     * del cliente y no pasan por un estado debounceado.
     */
    query: autocompleteQuery(state),
    inputValue: autocompleteInputValue(state, selectedLabel),
    onFocus: (event: FocusEvent<HTMLDivElement>) => {
      if (!focusCrossesTheControl(event.currentTarget, event.relatedTarget)) return;
      apply({ type: 'focus' });
    },
    onBlur: (event: FocusEvent<HTMLDivElement>) => {
      if (!focusCrossesTheControl(event.currentTarget, event.relatedTarget)) return;
      apply({ type: 'blur' });
    },
    onInputChange: (
      _event: unknown,
      text: string,
      reason: AutocompleteInputChangeReason,
    ) =>
      apply({ type: 'input', text, reason, selectedLabel: selectedLabelRef.current }),
    /**
     * ¿Se propaga este cambio de valor al formulario? Devuelve `false` para
     * el borrado que MUI dispara solo porque el input quedó vacío mientras
     * se tipeaba (ver invariante 2 de `autocompleteFocus.ts`); cuando
     * devuelve `true` además cierra la sesión de tipeo.
     */
    commitValueChange: (reason: AutocompleteChangeReason) => {
      if (!autocompleteAcceptsValueChange(latest.current, reason)) return false;
      apply({ type: 'valueChange', reason });
      return true;
    },
  };
}
